import { CustomSelectShadcn } from "../../components/custom-select-shadcn.component";
import { SelectProps } from "../../forms/form-types";
import { useCentralStore } from "../../store/Central";

interface PriceListSelectProps extends SelectProps {
	helperText?: string;
	emptyLabel?: string;
	showEmpty?: boolean;
	label?: string;
}

/**
 * PriceListSelect - select component for price lists
 * @param onChange - function to call when the value changes
 * @param value - the current value
 * @param emptyLabel - label for empty/null option (default: "Kein Standard")
 * @param showEmpty - whether to show empty option (default: true)
 * @example
 * <PriceListSelect
 *    onChange={(value: string | null) => handleValueChange("default_price_list_id", value)}
 *    value={formData.default_price_list_id}
 *    emptyLabel="Labor-Standard"
 * />
 */
export const PriceListSelect: React.FC<PriceListSelectProps> = ({
	emptyLabel = "Kein Standard",
	showEmpty = true,
	label = "Preisliste",
	...props
}) => {
	const priceLists = useCentralStore((state) => state.priceLists);

	// Filter out deactivated price lists and sort by name
	const activePriceLists = priceLists
		.filter((list) => !list.is_deactivated)
		.sort((a, b) => a.name.localeCompare(b.name));

	const options = [
		...(showEmpty ? [{ value: "", label: emptyLabel }] : []),
		...activePriceLists.map((list) => ({
			value: list.id,
			label: list.name,
		})),
	];

	return (
		<CustomSelectShadcn
			options={options}
			inputLabel={label}
			value={props.value || ""}
			onChange={(value) => {
				props.onChange(value?.toString() || null);
			}}
			disabled={props.disabled}
			densitySize={props.densitySize}
			inputHeight={props.inputHeight}
			width={props.width}
			required={props.required}
			validationError={props.validationError}
			helperText={props.helperText}
		/>
	);
};
